import { MAP_LINE_COLOR, MAP_LINE_STYLE_SOLID, MODE_AIR, MODE_WATER } from '@/lib/constants';
import { LocationInterface, MapPathInterface, MapRouteInterface, PositionInterface } from '@/types';
import { RoutePolylinesGroupApiInterface, SegmentModeType } from '@/types/api-types';
import { decodePolylinePath } from '@/utils/mapping';

const getMarkersArr = (mapRoute?: MapRouteInterface | null): LocationInterface[] => {
  if (!mapRoute?.markers?.length) return [];
  return mapRoute.markers.filter((marker) => marker);
};

// Creates an array of Path Objects to use for drawing Polylines
const createPathsArr = (
  mapRoute: MapRouteInterface | undefined | null,
  polylines: RoutePolylinesGroupApiInterface,
): MapPathInterface[] => {
  if (!mapRoute?.paths?.length) return [];
  return mapRoute.paths.map((path) => {
    const { id, key, color, style, locations, mode } = path;

    // Only use Polylines for Ocean and Road, even if one exists...
    const shouldUsePolyline = mode && mode !== MODE_AIR;
    // If we have a key, then lookup the polyline in the Store
    const routePolyline = key && shouldUsePolyline ? polylines?.[mode]?.[key] : null;

    // If it exists, decode the path
    const decodedPath =
      routePolyline && routePolyline.encodedPath && mode
        ? convertAndDecodePolyline(routePolyline.encodedPath, mode, locations)
        : null;

    // Use decoded path if it exists, use it, else fall back to a straight line
    const positions =
      decodedPath ||
      locations
        .map((location) => {
          return location?.location ? location.location : null;
        })
        .filter((position): position is PositionInterface => Boolean(position));

    return {
      id,
      color: color || MAP_LINE_COLOR,
      style: style || MAP_LINE_STYLE_SOLID,
      positions,
    };
  });
};

/**
 * Decodes an encoded Polyline and converts to a PositionInterface[]
 * @param encodedPath Encoded Polyline String
 * @returns PositionInterface[] or Null
 */
const convertAndDecodePolyline = (
  encodedPath: string,
  mode: SegmentModeType,
  locations: LocationInterface[],
): PositionInterface[] | null => {
  const isSeaPath = mode === MODE_WATER;
  const [start, end] = locations;
  const decodedPath = decodePolylinePath(encodedPath);

  if (decodedPath) {
    // If is seapath, add start and end points to join path to Seaport, else return as normal
    return isSeaPath
      ? ([start.location, ...decodedPath, end.location].filter(Boolean) as PositionInterface[])
      : decodedPath;
  }
  return null;
};

export { createPathsArr, getMarkersArr };
